import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import {
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Stack,
  IconButton,
  Slide,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import sidebarConfig from 'src/layouts/dashboard/SidebarConfig';
import useAuth from 'src/hooks/useAuth';
import { isCustomerUser, isPassengerUser } from 'src/utils/serviceTypes';
import { REST_API } from 'src/constants/DefaultValues';

const DEFAULT_SUPPORT_PHONE = '9677881500';
const EMERGENCY_PHONE = '112';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

export default function MobileBottomNavBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentChildren, setCurrentChildren] = useState([]);
  const [sosOpen, setSosOpen] = useState(false);
  const [supportPhone, setSupportPhone] = useState(DEFAULT_SUPPORT_PHONE);

  const showSosFeatures = isCustomerUser(user) || isPassengerUser(user);

  const fetchSupportPhone = useCallback(async () => {
    try {
      const response = await axios.get(`${REST_API}settings/get-settings`);
      if (response?.data?.status === 1) {
        const phone = response.data.data?.office_phone;
        if (phone) {
          setSupportPhone(String(phone).replace(/\s+/g, ''));
        }
      }
    } catch (error) {
      console.error('Error fetching SOS support phone:', error);
    }
  }, []);

  useEffect(() => {
    if (showSosFeatures) {
      fetchSupportPhone();
    }
  }, [showSosFeatures, fetchSupportPhone]);

  const handleOpenPopover = (event, children) => {
    setAnchorEl(event.currentTarget);
    setCurrentChildren(children);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setCurrentChildren([]);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClosePopover();
  };

  const handleCall = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
    setSosOpen(false);
  };

  if (!isMobile) return null;

  const navItems = sidebarConfig.flatMap((group) =>
    group.items.filter((item) => !item.hidden || !item.hidden(user))
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          zIndex: 1300,
          pt: 1.2,
          pb: 'calc(9.6px + env(safe-area-inset-bottom, 0px))',
          px: 1,
          overflowX: 'auto',
          overflowY: 'none',
        }}
      >
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const hasChildren = item.children && item.children.length > 0;
          const filteredChildren = item.children?.filter((c) => !c.hidden || !c.hidden(user)) || [];

          return (
            <Box
              key={item.title}
              onClick={(e) =>
                hasChildren ? handleOpenPopover(e, filteredChildren) : handleNavigate(item.path)
              }
              sx={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                mt: -0.5,
                WebkitTapHighlightColor: 'transparent',
                userSelect: 'none',
                outline: 'none',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: isActive ? 'primary.main' : 'action.selected',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '30%',
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    color: isActive ? 'common.white' : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  textTransform: 'capitalize',
                }}
              >
                {item.title === 'production control board'
                  ? isPassengerUser(user)
                    ? 'Home'
                    : 'PCB'
                  : item.title}
              </Typography>
            </Box>
          );
        })}

        {showSosFeatures && (
          <Box
            onClick={() => setSosOpen(true)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              mt: -0.5,
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              outline: 'none',
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                bgcolor: '#D32F2F',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease-in-out',
                borderRadius: '30%',
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  lineHeight: 1,
                  color: '#fff',
                }}
              >
                SOS
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={sosOpen}
        onClose={() => setSosOpen(false)}
        TransitionComponent={Transition}
        keepMounted
        fullWidth
        maxWidth="xs"
        aria-labelledby="sos-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 12px 36px rgba(0,0,0,0.16)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.75,
            background: 'linear-gradient(135deg, #F44336 0%, #C62828 100%)',
            color: '#FFF',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>
                SOS
              </Typography>
            </Box>
            <Box>
              <Typography
                id="sos-dialog-title"
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}
              >
                Emergency SOS
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Get help immediately
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => setSosOpen(false)}
            sx={{ color: '#FFF', p: 0.5 }}
          >
            <Close style={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}
          >
            Need help right now? Call Mayura support or national emergency services.
          </Typography>

          <Stack spacing={1.5}>
            <Box
              onClick={() => handleCall(supportPhone)}
              sx={{
                p: 1.5,
                bgcolor: '#f4f6f8',
                borderRadius: 1.5,
                border: '1px solid rgba(0,0,0,0.06)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(76, 175, 122, 0.08)',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Stack className="mdi--phone" sx={{ width: 20, height: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Call Mayura Support
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  {supportPhone}
                </Typography>
              </Box>
              <Stack
                className="material-symbols--phone-in-talk"
                sx={{ width: 18, height: 18, color: 'primary.main' }}
              />
            </Box>

            <Box
              onClick={() => handleCall(EMERGENCY_PHONE)}
              sx={{
                p: 1.5,
                bgcolor: '#f4f6f8',
                borderRadius: 1.5,
                border: '1px solid rgba(0,0,0,0.06)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(244, 67, 54, 0.08)',
                  borderColor: 'error.main',
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'error.lighter',
                  color: 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Stack className="mdi--alert-circle" sx={{ width: 20, height: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Call Emergency
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'error.main', fontWeight: 600 }}
                >
                  {EMERGENCY_PHONE}
                </Typography>
              </Box>
              <Stack
                className="material-symbols--phone-in-talk"
                sx={{ width: 18, height: 18, color: 'error.main' }}
              />
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            mt: -1,
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <List dense sx={{ minWidth: 180, pl: 1, pr: 1 }}>
          {currentChildren.map((child) => {
            const isActiveChild = location.pathname.startsWith(child.path);

            return (
              <ListItem
                button
                key={child.title}
                onClick={() => handleNavigate(child.path)}
                selected={isActiveChild}
                sx={{
                  px: 1.5,
                  py: 1,
                  mb: 0.5,
                  borderRadius: 0.8,
                  bgcolor: isActiveChild ? 'primary.main' : 'action.selected',
                  color: isActiveChild ? 'common.white' : 'text.secondary',
                  '&:last-of-type': {
                    mb: 0
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'common.white'
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                  }}
                >
                  {child.icon}
                </Box>
                <ListItemText
                  primary={
                    child.title.charAt(0).toUpperCase() + child.title.slice(1)
                  }
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
}
